import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Pencil, Trash2, UserCircle } from 'lucide-react';
import type { Member } from '@yp/types';
import { useAuth } from '../context/AuthContext';
import { getAdminMembers, deleteMember } from '../lib/api';

export default function MembersPage() {
  const { accessToken } = useAuth();
  const [members, setMembers] = useState<Member[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  async function fetchMembers() {
    if (!accessToken) return;
    setIsLoading(true);
    try {
      const { data } = await getAdminMembers(accessToken);
      setMembers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load members');
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    fetchMembers();
  }, [accessToken]);

  async function handleDelete(id: string, name: string) {
    if (!accessToken) return;
    if (!confirm(`Delete member "${name}"? This cannot be undone.`)) return;
    try {
      await deleteMember(accessToken, id);
      setMembers((prev) => prev.filter((m) => m.id !== id));
    } catch (err) {
      alert(err instanceof Error ? err.message : 'Delete failed');
    }
  }

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-100">Members</h1>
          <p className="mt-0.5 text-sm text-gray-500">{members.length} members total</p>
        </div>
        <Link
          to="/members/new"
          className="flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white"
          style={{ backgroundColor: 'var(--yp-pink)' }}
        >
          <Plus size={16} />
          Add Member
        </Link>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-20">
          <div className="h-6 w-6 animate-spin rounded-full border-2 border-pink-500 border-t-transparent" />
        </div>
      ) : error ? (
        <p className="rounded-lg bg-red-500/10 px-4 py-3 text-sm text-red-400">{error}</p>
      ) : members.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--color-border)] py-16 text-center">
          <p className="text-gray-500">No members yet.</p>
          <Link to="/members/new" className="mt-2 inline-block text-sm text-pink-400 hover:underline">
            Add the first member
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {members.map((member) => (
            <MemberCard
              key={member.id}
              member={member}
              onDelete={() => handleDelete(member.id, member.nickname)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function MemberCard({ member, onDelete }: { member: Member; onDelete: () => void }) {
  return (
    <div className="flex flex-col rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] overflow-hidden">
      <div className="relative aspect-[3/4] bg-black/30">
        {member.profile_photo ? (
          <img src={member.profile_photo} alt={member.nickname}
            className="h-full w-full object-cover" />
        ) : (
          <div className="flex h-full items-center justify-center">
            <UserCircle size={48} className="text-gray-600" />
          </div>
        )}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent px-3 pb-3 pt-6">
          <p className="font-semibold text-white">{member.nickname}</p>
          {member.full_name && <p className="text-xs text-gray-400">{member.full_name}</p>}
        </div>
      </div>

      <div className="p-3">
        <div className="mb-2 flex flex-wrap gap-1">
          <span className="rounded-full bg-pink-500/15 px-2 py-0.5 text-[10px] font-medium capitalize text-pink-400">
            {member.leadership_role ?? member.member_type}
          </span>
          {member.activity_status !== 'active' && (
            <span className="rounded-full bg-gray-500/15 px-2 py-0.5 text-[10px] font-medium text-gray-500 capitalize">
              {member.activity_status}
            </span>
          )}
        </div>

        {member.positions && member.positions.length > 0 && (
          <div className="mb-3 flex flex-wrap gap-1">
            {member.positions.slice(0, 3).map((pos) => (
              <span key={pos} className="rounded px-1.5 py-0.5 text-[10px] text-gray-400 bg-white/5">
                {pos.replace(/_/g, ' ')}
              </span>
            ))}
            {member.positions.length > 3 && (
              <span className="text-[10px] text-gray-600">+{member.positions.length - 3}</span>
            )}
          </div>
        )}

        <div className="flex gap-2">
          <Link to={`/members/${member.id}/edit`}
            className="flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-[var(--color-border)] py-1.5 text-xs text-gray-400 transition-colors hover:border-pink-500/30 hover:text-pink-400">
            <Pencil size={12} />
            Edit
          </Link>
          <button onClick={onDelete}
            className="flex items-center justify-center rounded-lg border border-[var(--color-border)] px-3 py-1.5 text-xs text-gray-400 transition-colors hover:border-red-500/30 hover:text-red-400">
            <Trash2 size={12} />
          </button>
        </div>
      </div>
    </div>
  );
}
